import { Approval, Entity, User } from "types";
import { defer, redirect } from "react-router-dom";
import {
  aggregate,
  createDirectus,
  readItems,
  createItem as sdkCreateItem,
  updateItem as sdkUpdateItem,
  deleteItem as sdkDeleteItem,
  readMe,
  rest,
} from "@directus/sdk";
import { API_URL, APP_URL } from "./constants";
import { isEmpty } from "lodash";

export const apiClient = createDirectus(API_URL).with(
  rest({ credentials: "include" })
);

export const logout = async () => {
  await fetch(`${API_URL}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ mode: "session" }),
  })
    .then(() => {
      window.location.href = APP_URL;
    })
    .catch((error) => {
      console.error("@@@ Error:", error);
    });
};

export function getScopes() {
  const SCOPES = process.env.REACT_APP_AZURE_SCOPES ?? "";
  return SCOPES.split(",").map((scope) => scope.trim());
}

export function withAuth<T>(func: (userPromise: User | null) => T) {
  return async () => {
    try {
      const res = await apiClient.request<User>(
        readMe({ fields: ["*", "role.name", "role.id"] })
      );
      return func(res);
    } catch (error) {
      return func(null);
    }
  };
}

enum USERS_HOME {
  "Super Admin" = "/home",
  Admin = "/home",
  QC = "/home",
  User = "/requests",
}

interface AuthLoaderOptions {
  redirectIfLoggedIn?: string;
  redirectIfLoggedOut?: string;
  roles?: User["role"]["name"][];
}
export async function authLoader(
  auth: User | null,
  options?: AuthLoaderOptions
) {
  if (auth) {
    if (options?.redirectIfLoggedIn) {
      return redirect(options.redirectIfLoggedIn);
    }
    if (
      options?.roles &&
      !options.roles.includes(auth.role.name) &&
      !isEmpty(options?.roles)
    ) {
      // redirect to the roles home
      return redirect(USERS_HOME[auth.role.name as keyof typeof USERS_HOME]);
    }

    return defer({ user: auth });
  }

  if (options?.redirectIfLoggedOut) {
    return redirect(options.redirectIfLoggedOut);
  }

  return !options?.redirectIfLoggedIn ? redirect("/") : null;
}

export async function getTotalEntityCount(
  entity: string,
  filter: Record<string, any> = {}
) {
  const totalCount = await apiClient.request(
    aggregate(entity, { aggregate: { count: "*" }, query: { filter } })
  );
  return parseInt(totalCount[0].count ?? "0");
}

export interface GetEntityListOptions {
  entity: Entity;
  page?: number;
  pageSize?: number;
  sort?: string[];
  fields?: string[];
  search?: string;
  paginate?: boolean;
  deep?: Record<string, any>;
  filter?: Record<string, any>;
}

export async function getEntityList<T extends Record<string, any>>({
  entity,
  page = 1,
  pageSize = 25,
  sort,
  fields,
  search = "",
  paginate = true,
  deep = {},
  filter = {},
}: GetEntityListOptions) {
  const totalCount = paginate ? await getTotalEntityCount(entity, filter) : 1;
  if (paginate && (page < 1 || page > Math.ceil(totalCount / pageSize))) {
    return;
  }
  const list = (await apiClient.request(
    readItems(entity, {
      limit: pageSize,
      offset: paginate ? (page - 1) * pageSize : 0,
      fields,
      sort,
      search,
      deep,
      filter,
    })
  )) as T[];

  return {
    list,
    totalCount,
    pages: paginate ? Math.ceil(totalCount / pageSize) : 1,
  };
}

// Typescript types for the directus create and update item functions are problematic
export async function createItem<I>(
  entity: Entity,
  item: I,
  query?: any
): Promise<I> {
  const createdItem = sdkCreateItem(entity as never, item as never, query);
  return await apiClient.request(createdItem as any);
}

export async function updateItem<I>(
  entity: Entity,
  item: any,
  query?: any
): Promise<I> {
  const createdItem = sdkUpdateItem(
    entity as never,
    item.id,
    item as never,
    query
  );
  return await apiClient.request(createdItem as any);
}

export async function deleteItem<I>(entity: Entity, id: string): Promise<I> {
  const createdItem = sdkDeleteItem(entity as never, id);
  return await apiClient.request(createdItem as any);
}

export const hasUserApproved = (approvals: Approval[], userId: string) => {
  return approvals.some((approval) => approval.user_created?.id === userId);
};

export const hasApprovalRejection = (approvals: Approval[]) => {
  return approvals.some((approval) => !approval.satisfactory);
};
